import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle, XCircle, AlertCircle, CheckCircle } from 'lucide-react';

// Warning types enum
export const WARNING_TYPES = {
  EXPIRED: 'expired',
  EXPIRING_SOON: 'expiring_soon',
  JOB_TYPE_NOT_APPROVED: 'job_type_not_approved',
};

const ComplianceWarningModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  warningType,
  medicName,
  medicId: _medicId,
  jobTypeName,
}) => {
  const isExpired = warningType === WARNING_TYPES.EXPIRED;
  const isExpiringSoon = warningType === WARNING_TYPES.EXPIRING_SOON;
  const isJobTypeWarning = warningType === WARNING_TYPES.JOB_TYPE_NOT_APPROVED;
  
  const getStatusIcon = () => {
    if (isExpired) return <XCircle className="w-12 h-12 text-red-400" />;
    if (isExpiringSoon) return <AlertTriangle className="w-12 h-12 text-[#FFC04D]" />;
    if (isJobTypeWarning) return <AlertCircle className="w-12 h-12 text-indigo-400" />;
    return <AlertCircle className="w-12 h-12 text-[#FFC04D]" />;
  };

  const getStatusColor = () => {
    if (isExpired) return 'border-red-500/20 bg-red-500/10';
    if (isJobTypeWarning) return 'border-indigo-500/20 bg-indigo-500/10';
    return 'border-[#FFC04D]/20 bg-[#FFC04D]/10';
  };

  const getTitle = () => {
    if (isExpired) return 'Medic Not Compliant';
    if (isExpiringSoon) return 'Documents Expiring Soon';
    if (isJobTypeWarning) return 'Medic Not Approved for This Job Type';
    return 'Compliance Warning';
  };

  const getMessage = () => {
    if (isExpired) {
      return 'This medic has one or more expired compliance documents. Please contact them to provide updated documentation. You may continue with the booking after acknowledging this warning.';
    }
    if (isExpiringSoon) {
      return 'This medic has compliance documents that will expire within the next 30 days. Please ensure updated documents are requested. You may continue with the booking.';
    }
    if (isJobTypeWarning) {
      return 'This medic is not currently approved for the selected job type. Please confirm they are suitable before assigning them to this shift.';
    }
    return 'Please review the compliance status before proceeding.';
  };

  const getConfirmButtonText = () => {
    if (isExpiringSoon) return 'Continue';
    return 'Acknowledge & Continue';
  };

  const getConfirmButtonStyle = () => {
    if (isExpired) return 'bg-red-500 hover:bg-red-600 text-white';
    if (isJobTypeWarning) return 'bg-indigo-500 hover:bg-indigo-600 text-white';
    return 'bg-[#FFC04D] hover:bg-[#FFC04D]/90 text-black';
  };

  const getMessageBoxStyle = () => {
    if (isExpired) return 'border-red-500/20 bg-red-500/5';
    if (isJobTypeWarning) return 'border-indigo-500/20 bg-indigo-500/5';
    return 'border-[#FFC04D]/20 bg-[#FFC04D]/5';
  };

  const getMessageTextStyle = () => {
    if (isExpired) return 'text-red-300';
    if (isJobTypeWarning) return 'text-indigo-300';
    return 'text-[#FFC04D]';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2] max-w-md">
        <DialogHeader>
          <div className={`mx-auto p-4 rounded-full ${getStatusColor()} mb-4`}>
            {getStatusIcon()}
          </div>
          <DialogTitle className="text-center text-xl">
            {getTitle()}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {medicName && (
            <div className="text-center">
              <span className="text-[#A1A1AA]">Medic: </span>
              <span className="text-[#F2F2F2] font-medium">{medicName}</span>
            </div>
          )}

          {isJobTypeWarning && jobTypeName && (
            <div className="text-center">
              <span className="text-[#A1A1AA]">Job Type: </span>
              <span className="text-[#F2F2F2] font-medium">{jobTypeName}</span>
            </div>
          )}
          
          <div className={`p-4 rounded-lg border ${getMessageBoxStyle()}`}>
            <p className={`text-sm ${getMessageTextStyle()}`}>
              {getMessage()}
            </p>
          </div>
        </div>
        
        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button 
            variant="ghost" 
            onClick={onClose} 
            className="text-[#A1A1AA] flex-1"
            data-testid="compliance-warning-cancel-btn"
          >
            Cancel
          </Button>
          <Button 
            onClick={onConfirm} 
            className={`flex-1 ${getConfirmButtonStyle()}`}
            data-testid="compliance-warning-confirm-btn"
          >
            {(isExpired || isJobTypeWarning) && <CheckCircle className="w-4 h-4 mr-2" />}
            {getConfirmButtonText()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ComplianceWarningModal;
